import React, { useState } from 'react'
import { toast } from 'react-toastify'

const AdminSettings = () => {
  const [settings, setSettings] = useState({
    adminEmails: [],
    maintenanceMode: false,
    registrationEnabled: true,
    emailNotifications: true,
    maxUsersPerPlan: {
      free: 1000,
      pro: 10000,
      business: -1 // unlimited
    }
  })
  const [loading, setLoading] = useState(false)
  const [newAdminEmail, setNewAdminEmail] = useState('')

  const handleSaveSettings = async () => {
    try {
      setLoading(true)
      // In a real implementation, you would save these to the database
      toast.success('Settings saved successfully (UI only - database integration pending)')
    } catch (error) {
      console.error('Error saving settings:', error)
      toast.error('Failed to save settings')
    } finally {
      setLoading(false)
    }
  }

  const addAdminEmail = () => {
    if (!newAdminEmail || !newAdminEmail.includes('@')) {
      toast.error('Please enter a valid email address')
      return
    }

    if (settings.adminEmails.includes(newAdminEmail)) {
      toast.error('Email already exists in admin list')
      return
    }

    setSettings(prev => ({
      ...prev,
      adminEmails: [...prev.adminEmails, newAdminEmail]
    }))
    setNewAdminEmail('')
    toast.success('Admin email added')
  }

  const removeAdminEmail = (email) => {
    setSettings(prev => ({
      ...prev,
      adminEmails: prev.adminEmails.filter(e => e !== email)
    }))
    toast.success('Admin email removed')
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Admin Settings</h1>
        <p className="text-gray-600">Configure application settings and preferences</p>
      </div>

      <div className="space-y-6">
        {/* Admin Management */}
        <div className="bg-white rounded-lg p-6 shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Admin Management</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Admin Email Addresses
              </label>
              <div className="flex space-x-2 mb-3">
                <input
                  type="email"
                  value={newAdminEmail}
                  onChange={(e) => setNewAdminEmail(e.target.value)}
                  placeholder="Enter admin email"
                  className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  onClick={addAdminEmail}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                >
                  Add
                </button>
              </div>
              
              <div className="space-y-2">
                {settings.adminEmails.map((email, index) => (
                  <div key={index} className="flex justify-between items-center bg-gray-50 p-2 rounded">
                    <span className="text-sm">{email}</span>
                    <button
                      onClick={() => removeAdminEmail(email)}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Application Settings */}
        <div className="bg-white rounded-lg p-6 shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Application Settings</h3>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Maintenance Mode</label>
                <p className="text-sm text-gray-500">Temporarily disable access to the application</p>
              </div>
              <button
                onClick={() => setSettings(prev => ({ ...prev, maintenanceMode: !prev.maintenanceMode }))}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  settings.maintenanceMode ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition duration-200 ease-in-out ${
                    settings.maintenanceMode ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">User Registration</label>
                <p className="text-sm text-gray-500">Allow new users to register</p>
              </div>
              <button
                onClick={() => setSettings(prev => ({ ...prev, registrationEnabled: !prev.registrationEnabled }))}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  settings.registrationEnabled ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition duration-200 ease-in-out ${
                    settings.registrationEnabled ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Email Notifications</label>
                <p className="text-sm text-gray-500">Send admin email notifications for important events</p>
              </div>
              <button
                onClick={() => setSettings(prev => ({ ...prev, emailNotifications: !prev.emailNotifications }))}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  settings.emailNotifications ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition duration-200 ease-in-out ${
                    settings.emailNotifications ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Subscription Limits */}
        <div className="bg-white rounded-lg p-6 shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Subscription Limits</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Free Plan Max Users
              </label>
              <input
                type="number"
                value={settings.maxUsersPerPlan.free}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  maxUsersPerPlan: { ...prev.maxUsersPerPlan, free: parseInt(e.target.value) }
                }))}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pro Plan Max Users
              </label>
              <input
                type="number"
                value={settings.maxUsersPerPlan.pro}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  maxUsersPerPlan: { ...prev.maxUsersPerPlan, pro: parseInt(e.target.value) }
                }))}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Business Plan Max Users
              </label>
              <input
                type="number"
                value={settings.maxUsersPerPlan.business === -1 ? '' : settings.maxUsersPerPlan.business}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  maxUsersPerPlan: { 
                    ...prev.maxUsersPerPlan, 
                    business: e.target.value === '' ? -1 : parseInt(e.target.value) 
                  }
                }))}
                placeholder="Unlimited (-1)"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* System Information */}
        <div className="bg-white rounded-lg p-6 shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">System Information</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Application Version:</span>
              <span className="ml-2 font-medium">v2.1.0</span>
            </div>
            <div>
              <span className="text-gray-600">Database Status:</span>
              <span className="ml-2 font-medium text-green-600">Connected</span>
            </div>
            <div>
              <span className="text-gray-600">Last Backup:</span>
              <span className="ml-2 font-medium">2026-01-04 02:00 UTC</span>
            </div>
            <div>
              <span className="text-gray-600">Storage Used:</span>
              <span className="ml-2 font-medium">2.4 GB / 100 GB</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Reset
          </button>
          <button
            onClick={handleSaveSettings}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default AdminSettings